import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { api, ApiError } from "./api";

const TOKEN_KEY = "portal_token";

interface PortalUser {
    id: string;
    email: string;
    displayName: string | null;
    customerId: string;
    customerName: string;
    /** The office shared this customer's budget with their portal. */
    showBudget?: boolean;
}

interface PortalAuthState {
    user: PortalUser | null;
    loading: boolean;
}

interface PortalAuthContextValue extends PortalAuthState {
    requestMagicLink: (email: string) => Promise<{ devToken?: string }>;
    verifyToken: (token: string) => Promise<void>;
    logout: () => void;
}

function getPortalToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
}

function setPortalToken(token: string): void {
    localStorage.setItem(TOKEN_KEY, token);
}

function clearPortalToken(): void {
    localStorage.removeItem(TOKEN_KEY);
}

function portalHeaders(): HeadersInit {
    const token = getPortalToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
}

async function portalRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has("Accept")) headers.set("Accept", "application/json");
    if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
    Object.entries(portalHeaders()).forEach(([k, v]) => {
        if (!headers.has(k)) headers.set(k, v);
    });

    const base = import.meta.env.VITE_API_ORIGIN?.trim()?.replace(/\/$/, "") ?? "";
    const url = path.startsWith("/") ? `${base}${path}` : `${base}/${path}`;
    const res = await fetch(url, { ...init, headers });
    const text = await res.text();
    let body: unknown = text;
    try {
        body = JSON.parse(text);
    } catch {
        /* raw text */
    }
    if (res.status === 401) {
        // An expired/invalid portal session must bounce the customer to
        // login instead of leaving every page stuck on an empty state.
        // Skip the auth/boot endpoints so the magic-link verify + initial
        // /me probe keep their own handling and we can't loop.
        const isAuthEndpoint = path.includes("/api/portal/auth/") || path.includes("/api/portal/me");
        if (!isAuthEndpoint && typeof window !== "undefined") {
            clearPortalToken();
            if (window.location.pathname !== "/portal/login") {
                window.location.href = "/portal/login";
            }
        }
    }
    if (!res.ok) throw new ApiError(res.status, res.statusText, body);
    return body as T;
}

export const portalApi = {
    get: <T,>(path: string) => portalRequest<T>(path),
    post: <T,>(path: string, data?: unknown) =>
        portalRequest<T>(path, {
            method: "POST",
            body: data ? JSON.stringify(data) : undefined,
        }),
};

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

export function PortalAuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<PortalAuthState>({
        user: null,
        loading: !!getPortalToken(),
    });

    const fetchMe = useCallback(async () => {
        try {
            const data = await portalApi.get<{ user: PortalUser }>("/api/portal/me");
            setState({ user: data.user, loading: false });
        } catch (err) {
            clearPortalToken();
            setState({ user: null, loading: false });
            if (err instanceof ApiError && err.status === 401) return;
            console.error("Portal auth check failed:", err);
        }
    }, []);

    useEffect(() => {
        if (getPortalToken()) fetchMe();
    }, [fetchMe]);

    const requestMagicLink = async (email: string) => {
        const data = await api.post<{ ok: boolean; _devToken?: string }>("/api/portal/auth/magic-link", { email });
        return { devToken: data._devToken };
    };

    const verifyToken = async (token: string) => {
        const data = await portalApi.post<{ token: string; user: PortalUser }>("/api/portal/auth/verify", { token });
        setPortalToken(data.token);
        setState({ user: data.user, loading: false });
    };

    const logout = () => {
        clearPortalToken();
        setState({ user: null, loading: false });
    };

    return (
        <PortalAuthContext.Provider value={{ ...state, requestMagicLink, verifyToken, logout }}>
            {children}
        </PortalAuthContext.Provider>
    );
}

export function usePortalAuth(): PortalAuthContextValue {
    const ctx = useContext(PortalAuthContext);
    if (!ctx) throw new Error("usePortalAuth must be used within PortalAuthProvider");
    return ctx;
}
